
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

export const useMapInit = () => {
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    let checkAttemptsCount = 0;
    const maxAttempts = 30; // About 3 seconds of attempts
    
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps) {
        console.log("Google Maps API loaded successfully");
        setIsGoogleMapsLoaded(true);
        return;
      } 
      
      checkAttemptsCount++;
      if (checkAttemptsCount > maxAttempts) {
        console.error("Google Maps failed to load after multiple attempts");
        setMapError("Google Maps couldn't be loaded. Using alternative map.");
        return;
      }
      
      setTimeout(checkGoogleMapsLoaded, 100);
    };
    
    // Function to handle quota and authorization errors
    const handleMapErrors = () => {
      // Check for quota errors and RefererNotAllowed errors
      const checkForErrors = () => {
        // Look for error elements that Google Maps injects into the DOM
        const errorElements = document.querySelectorAll('.gm-err-message, .gm-err-content');
        
        if (errorElements.length > 0) {
          // Try to extract the specific error message
          let errorMsg = "Google Maps API error detected.";
          
          // Look for the specific error text
          errorElements.forEach(el => {
            const text = el.textContent || '';
            console.log("Google Maps error element text:", text);
            
            if (text.includes("RefererNotAllowedMapError")) {
              // Extract the URL that needs to be authorized if present
              const urlMatch = /to be authorized: (https:\/\/[^\s]+)/.exec(text) || 
                               text.match(/site URL to be authorized: (https:\/\/[^\s]+)/);
              const siteUrl = urlMatch ? urlMatch[1] : window.location.origin + window.location.pathname;
              
              errorMsg = `RefererNotAllowedMapError: Your site URL to be authorized: ${siteUrl}`;
            } else if (text.includes("QuotaExceeded")) {
              errorMsg = "Google Maps API quota exceeded. Using alternative map.";
            } else if (text.includes("InvalidKey")) {
              errorMsg = "Invalid Google Maps API key. Using alternative map.";
            } else if (text.includes("ApiNotActivated")) {
              errorMsg = "Google Maps API not activated for this project. Using alternative map.";
            } else if (text.length > 5) {
              // If we have some text but don't recognize it, use it as the error message
              errorMsg = text;
            }
          });
          
          console.error(errorMsg);
          setMapError(errorMsg);
          toast({
            title: "Map Error",
            description: "Google Maps failed to load. Using alternative map.",
            variant: "destructive",
          });
          return true;
        }
        return false;
      };
      
      // Check immediately
      if (!checkForErrors()) {
        // Check again after a delay to catch errors that appear after initial load
        setTimeout(checkForErrors, 1000);
      }
    };
    
    // Start checking for Maps loading
    checkGoogleMapsLoaded();
    
    // Check for quota and auth errors
    setTimeout(handleMapErrors, 500);

    // Set up global authentication failure handler
    const handleGoogleMapsAuthError = (event: CustomEvent) => {
      console.error("Google Maps authentication failed via gm_authFailure callback");
      
      const siteUrl = event.detail?.url || window.location.origin + window.location.pathname;
      const errorMsg = `Google Maps failed to load due to authentication issues. Your site URL to be authorized: ${siteUrl}`;
      
      setMapError(errorMsg);
      toast({
        title: "Map Error",
        description: "Google Maps authentication failed. Using alternative map.",
        variant: "destructive",
      });
    };

    // Listen for custom error event from our onerror handler in index.html
    const handleCustomGoogleMapsError = () => {
      console.error("Google Maps failed to load (custom error event)");
      setMapError("Google Maps API failed to load. Using alternative map.");
      toast({
        title: "Map Error",
        description: "Google Maps failed to load. Using alternative map.",
        variant: "destructive",
      });
    };
    
    document.addEventListener('google-maps-error', handleCustomGoogleMapsError);
    document.addEventListener('google-maps-auth-error', handleGoogleMapsAuthError as EventListener);

    // Listen for JavaScript errors related to Google Maps
    const handleJSError = (event: ErrorEvent) => {
      if (event.message && 
          (event.message.includes("Google Maps") || 
           event.message.includes("google.maps") ||
           event.message.includes("OverQuotaMapError") ||
           event.message.includes("RefererNotAllowedMapError"))) {
        console.error("Google Maps error detected in JS:", event.message);
        setMapError(`Google Maps API error. Using alternative map.`);
        toast({
          title: "Map Error",
          description: "Google Maps failed to load. Using alternative map.",
          variant: "destructive",
        });
      }
    };
    
    window.addEventListener('error', handleJSError);

    return () => {
      window.removeEventListener('error', handleJSError);
      document.removeEventListener('google-maps-error', handleCustomGoogleMapsError);
      document.removeEventListener('google-maps-auth-error', handleGoogleMapsAuthError as EventListener);
    };
  }, [toast]);

  return { isGoogleMapsLoaded, mapError };
};
